import axios, { AxiosError } from "axios";
import Cookies from "js-cookie";
import { useAuthStore } from "@/stores";
import { decamelizeKeys, camelizeKeys } from "humps";
import { ref, computed } from "vue";
import { validationErrorMessages } from "@/utils/validationErrorMessages";
// The executor automatically converts the case of JSON props
// to match the python\javascript convention before\after
// sending the request. You can disable this behavior
// by setting config._skipPropsAdaptation to true.

const executor = axios.create({
  baseURL: "/api",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
    "X-CSRFToken": Cookies.get("csrftoken"),
  },
});

const authHeader = "Authorization";
const ACTIVE_COMPANY_NOT_PERMITTED = "active_company_not_permitted";
// Backend refused a fax send because the signer's saved signature is a legacy raster that fades out
// in transmission (portal/api/utils.py SIGNATURE_REDRAW_REQUIRED_CODE). Exported because the remedy
// is a UI action — re-draw in the signature pad — not just a message to print.
export const SIGNATURE_REDRAW_REQUIRED = "signature_redraw_required";
let recoveringActiveCompany = false;
const ensureTrailingSlash = (s) => s + (s.endsWith("/") ? "" : "/");

/**
 * The auth headers every request carries. Single source so a request that cannot go through the
 * executor still addresses the same user acting as the same company — dropping X-Active-Company
 * makes a switched user's request resolve nothing and 404.
 * @returns {Record<string, string>}
 */
const authRequestHeaders = () => {
  const authStore = useAuthStore();
  const headers = {};
  if (authStore.userInfo.access) {
    headers[authHeader] = `Bearer ${authStore.userInfo.access}`;
  }
  if (authStore.activeCompanyId) {
    headers["X-Active-Company"] = authStore.activeCompanyId;
  }
  return headers;
};

/**
 * DELETE that must outlive the page. The browser cancels the executor's in-flight XHRs once
 * `beforeunload` returns; `keepalive` hands the request to the browser to finish on its own.
 * @param {string} path - API path without the `/api` prefix
 * @returns {Promise<void>}
 */
const deleteOnUnload = (path) =>
  fetch(`${executor.defaults.baseURL}/${path}`, {
    method: "DELETE",
    keepalive: true,
    headers: authRequestHeaders(),
  }).catch(() => {});

executor.interceptors.request.use(
  (config) => {
    if (config._noAuthHeader) {
      return config;
    }

    for (const [name, value] of Object.entries(authRequestHeaders())) {
      config.headers[name] = value;
    }

    if (!config._skipPropsAdaptation) {
      config.data = decamelizeKeys(config.data);
      config.params = decamelizeKeys(config.params);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

executor.interceptors.response.use(
  (res) => {
    // removing redundant data property
    updateAuthHeader(res);
    if (res.config._returnFullResponse) return res;

    return res.config._skipPropsAdaptation ? res.data : camelizeKeys(res.data);
  },
  async (err) => {
    const config = err.config;
    updateAuthHeader(err.response);
    if (err.response?.status == 403 && err.response?.data?.error_code === ACTIVE_COMPANY_NOT_PERMITTED) {
      const authStore = useAuthStore();
      authStore.setActiveCompany(null);
      if (!recoveringActiveCompany) {
        recoveringActiveCompany = true;
        window.location.reload();
      }
      throw new ApiError(err);
    }

    // The backend middleware already refreshes an expired access token from the refresh cookie.
    // A 401 on a logged-in session is retried once: auth rejects before the view runs, so the retry repeats
    // no work, and the middleware renews a token that ran out mid-request. A second 401 means the session is
    // dead, so redirect once. Before the second sign-in step a 401 is a wrong OTP, and a 403 is a permission
    // denial on a live session; both are the caller's to handle.
    const authStore = useAuthStore();
    if (config?.headers?.[authHeader] && err.response?.status == 401 && authStore.loggedIn) {
      if (!config._authRetried) {
        config._authRetried = true;
        return executor(config);
      }
      authStore.handleSessionExpired();
    }

    throw new ApiError(err);
  }
);

/**
 * Update the auth header with the new access token if it is present in the response headers
 * @param {AxiosResponse} response - The response from the API call
 */
const updateAuthHeader = (response) => {
  try {
    const newAccessToken = response.headers["x-updated-access-token"];
    if (newAccessToken) {
      useAuthStore().setNewAccessToken(newAccessToken);
    }
  } catch {
    // Could not parse the response headers, continue
  }
};

executor.getPrefixedMethods = function (urlPrefix) {
  const newApi = {};
  urlPrefix = ensureTrailingSlash(urlPrefix);

  for (const m of ["get", "post", "put", "delete", "patch"]) {
    newApi[m] = (url, ...args) => executor[m](ensureTrailingSlash(urlPrefix + url), ...args);
  }
  newApi.deleteOnUnload = (url) => deleteOnUnload(ensureTrailingSlash(urlPrefix + url));

  return newApi;
};

export class ApiError extends AxiosError {
  constructor(error) {
    super(error.message, error.code, error.config, error.request, error.response);
    this.error = error;
    this.message = this.format();
  }

  _formatFieldName(fieldName) {
    return fieldName.replace(/_/g, " ");
  }

  format(asList = false) {
    const detail = this.error.response?.data?.error_details;
    const validationErrors = this.error.response?.data?.validation_errors;
    const nonFieldErrors = this.error.response?.data?.non_field_errors;
    let messageList = [detail];

    if (validationErrors) {
      if (Array.isArray(validationErrors)) {
        messageList = messageList.concat(validationErrorMessages(validationErrors));
      } else {
        messageList.push("Invalid fields:");
        for (const fieldName in validationErrors) {
          const messages = validationErrorMessages(validationErrors[fieldName]).join(", ");
          messageList.push(`${this._formatFieldName(fieldName)}: ${messages}`);
        }
      }
    }
    if (nonFieldErrors) {
      messageList.push("Errors:");
      messageList.push(nonFieldErrors.join(", "));
    }

    messageList = messageList.filter(Boolean);
    if (!messageList.length) {
      const status = this.error.response?.status;
      const data = this.error.response?.data;
      // Legacy {"error": "..."} and DRF {"detail": "..."} payloads carry the server-authored
      // reason; 5xx bodies stay suppressed so internal exception text never reaches the user.
      const serverMessage = status && status < 500 ? data?.error ?? data?.detail : undefined;
      if (serverMessage) {
        messageList = [String(serverMessage)];
      } else {
        messageList = [
          status
            ? `The server could not process the request (HTTP ${status})`
            : "Could not reach the server — check your connection",
        ];
      }
    }

    return asList ? messageList : messageList.join("\n ");
  }
}

/**
 * A composable that provides pagination functionality for API calls.
 * @param {Function} apiMethod - The API method to use for pagination, must take a pagingOptions object as the last parameter
 * @param {...any} params - Additional parameters to pass to the API method
 * @returns {Any} An object containing pagination methods and state
 * @property {Function} next - Fetches the next page of results
 * @property {Function} previous - Fetches the previous page of results
 * @property {Function} fetch - Fetches a specific page of results, defaults to the first page
 * @property {ComputedRef<boolean>} hasPrevious - Whether there is a previous page
 * @property {ComputedRef<boolean>} hasNext - Whether there is a next page
 */
export const useApiPaginator = (apiMethod, ...params) => {
  const nextPage = ref(1);
  const previousPage = ref(null);
  const hasPrevious = computed(() => previousPage.value !== null);
  const hasNext = computed(() => nextPage.value !== null);
  const pageSize = ref(undefined); // undefined means no  page size override

  const next = () => {
    if (!hasNext.value) return;
    return fetch({ page: nextPage.value, page_size: pageSize.value });
  };
  const previous = () => {
    if (!hasPrevious.value) return;
    return fetch({ page: previousPage.value, page_size: pageSize.value });
  };

  const fetch = (pagingOptions) => {
    return apiMethod(...params, pagingOptions).then((response) => {
      _parseResponse(response);
      return response;
    });
  };

  const _parseResponse = (response) => {
    if (response.next) {
      nextPage.value = new URL(response.next).searchParams.get("page");
    } else {
      nextPage.value = null;
    }
    if (response.previous) {
      // The first page url has no page value, so we set it to 1
      previousPage.value = new URL(response.previous).searchParams.get("page") || 1;
    } else {
      previousPage.value = null;
    }
  };

  return {
    next,
    previous,
    fetch,
    hasPrevious,
    hasNext,
  };
};

export default executor;
